import clsx from "clsx";
import maplibregl, { Marker } from "maplibre-gl";
import React, { useCallback, useEffect, useRef } from "react";
import { emitter } from "~/utils/emmitter";
import CallToAction from "~/components/CallToAction";
import { type Continent, type LocationType } from "./MapWrapper";
import { type LocationData } from "~/slices/LocationsSection";
import { Protocol } from "pmtiles";

// Zoom and lat & long when there is no active continent.
const DEFAULT_ZOOM = 1.2;
const DEFAULT_LATITUDE_AND_LONGITUDE: [number, number] = [5, 10];

const continents = [
  {
    name: "Europe",
    geopoint: {
      latitude: 49.863436848021834,
      longitude: 6.611782699413548,
    },
    isBigger: true,
    width: 10,
    height: 10,
  },
  {
    name: "Australia",
    geopoint: {
      latitude: -25.78439893835864,
      longitude: 134.0185219094149,
    },
    isBigger: false,
    width: 3,
    height: 3,
  },
  {
    name: "North America",
    geopoint: {
      latitude: 39.232253141714914,
      longitude: -99.228515625,
    },
    isBigger: true,
    width: 10,
    height: 10,
  },
  {
    name: "Asia",
    geopoint: {
      latitude: 29,
      longitude: 100,
    },
    isBigger: true,
    width: 10,
    height: 10,
  },
];

export default function FlatMap({
  locations,
  updateContinentHandler,
  setActiveLocationHandler,
  isLocationOverview,
}: {
  // continents: Dictionary<LocationData[]>;
  locations: LocationData[];
  updateContinentHandler: (continent: Continent) => void;
  setActiveLocationHandler: (location: string) => void;
  isLocationOverview: boolean;
}) {
  const mapContainer = useRef(null);

  // Because latitude and longitude of the continents are not given from the CMS, we would have to
  // custom set them over here, so this function serves to return the lat,lng and the amount of zoom needed for each continent
  const getLngLatAndZoomBasedOnActiveContinent = useCallback(
    (
      continent: Continent,
      geopoint: { latitude?: number | null; longitude?: number | null },
    ) => {
      // basic centerAndZoom for world view
      const centerAndZoom = {
        center:
          geopoint?.latitude && geopoint?.longitude
            ? [geopoint.latitude, geopoint.longitude]
            : [...DEFAULT_LATITUDE_AND_LONGITUDE],
        zoom:
          window.innerWidth < 1280
            ? window.innerWidth < 768
              ? -0.5
              : 0.5
            : DEFAULT_ZOOM,
      };

      // if it's an active continent, zoom to 3
      if (continent !== "All facilities") {
        centerAndZoom.zoom = 3;
      }

      return centerAndZoom;
    },
    [],
  );

  // Each continent's marker has a different size, and this function returns the width and the height
  // based on the total locations within a continent
  // const getWidthAndHeightBasedOnContinentSize = (
  //   amountOfLocationsWithinContinent: number,
  // ) => {
  //   if (amountOfLocationsWithinContinent <= 3)
  //     return { isBigger: false, width: 3, height: 3 };
  //   else return { isBigger: true, width: 10, height: 10 };
  // };

  // Maps through each continent and puts a marker on the map
  const addContinentMarkers = useCallback(
    (locations: LocationData[], map: maplibregl.Map) => {
      for (const continent of continents) {
        if (continent?.name == "All facilities") {
          continue;
        } else {
          const continentName = continent?.name ?? null;
          const continentGeoLocation = continent?.geopoint ?? {
            latitude: DEFAULT_LATITUDE_AND_LONGITUDE[0],
            longitude: DEFAULT_LATITUDE_AND_LONGITUDE[1],
          };
          const continentLength =
            locations.filter((location) => location.continent == continentName)
              .length ?? 0;

          const { height, width, isBigger } = continent;

          const { center } = getLngLatAndZoomBasedOnActiveContinent(
            continentName as Continent,
            continentGeoLocation,
          );

          const parent = document.createElement("div");
          parent.className =
            "z-20 flex items-center justify-center group hover:z-30";

          const outerEl = document.createElement("div");
          outerEl.className = `w-${width} h-${height} bg-blue/40 border border-blue/60 cursor-pointer rounded-full flex justify-center items-center transition-all duration-scalingCursor ${
            isBigger ? "p-16 md:p-24" : "p-12 md:p-16"
          }
          }`;
          outerEl.innerHTML = `
          <div class="w-3 h-3 p-3 rounded-full overflow-hidden bg-blue ${
            isBigger ? "group-hover:p-20" : "group-hover:p-14"
          } relative origin-center transition-all duration-scalingCursor flex flex-col justify-center items-center">
                  <span class="text-center text-body text-white transition-opacity opacity-0 group-hover:opacity-100 duration-scalingCursor relative">
                    ${continentName}
                    <span class="text-white/80 transition-opacity opacity-0 group-hover:opacity-100 duration-scalingCursor text-small-body mb-0 ml-1 absolute -top-3">${continentLength}</span>
                  </span>
          </div>`;

          parent.appendChild(outerEl);
          parent.addEventListener("click", () => {
            updateContinentHandler(continentName as Continent);
          });

          new Marker({ element: parent })
            .setLngLat({
              lng: center[1]!,
              lat: center[0]!,
            })
            .addTo(map);
        }
      }
    },
    [getLngLatAndZoomBasedOnActiveContinent, updateContinentHandler],
  );

  // Maps through each location of a continent and puts markers on the map
  const addLocationMarkersOnTheMap = useCallback(
    (locations: LocationData[], map: maplibregl.Map) => {
      for (const locationData of locations) {
        if (!locationData) return null;
        makeLocationMapMarker(map, locationData);
      }
    },
    [],
  );

  function makeLocationMapMarker(
    map: maplibregl.Map,
    locationData: LocationData,
  ) {
    if (!map) return;
    const { latitude, longitude } = {
      latitude: locationData?.position?.latitude ?? 15,
      longitude: locationData?.position?.longitude ?? -11.5,
    };

    const parent = document.createElement("div");
    parent.className = "z-20 flex items-center justify-center group hover:z-30";

    const outerEl = document.createElement("div");
    outerEl.className = `${
      locationData.type?.[0] !== "Office"
        ? `bg-blue/40 border-blue/60`
        : `bg-slate-grey/40 border-slate-grey/60`
    } p-2.5 border cursor-pointer rounded-full flex justify-center items-center transition-all duration-scalingCursor group-hover:p-4`;
    outerEl.innerHTML = `
        <div class="w-1 h-1 rounded-full overflow-hidden ${
          locationData.type?.[0] !== "Office" ? `bg-blue` : `bg-slate-grey`
        } group-hover:w-32 group-hover:h-32 relative origin-center transition-all duration-scalingCursor flex flex-col justify-center items-center">
        <div class="absolute flex flex-col items-center justify-center top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
          <span class="text-center text-body text-white transition-opacity opacity-0 w-28 group-hover:opacity-100 duration-scalingCursor relative">
            ${locationData.name}
          </span>
          <span class="text-white/80 transition-opacity opacity-0 w-28 group-hover:opacity-100 duration-scalingCursor text-small-body text-center">${
            locationData.country
          }</span>
        </div>
        </div>`;

    parent.appendChild(outerEl);
    parent.addEventListener("click", () => {
      setActiveLocationHandler(locationData.name!);
    });

    const marker = new Marker({ element: parent })
      .setLngLat({
        lng: longitude,
        lat: latitude,
      })
      .addTo(map);
  }

  function checkToFlyToContinent(continent: Continent, map: maplibregl.Map) {
    // get geopoint of current active continent
    const geopoint = locations?.filter(
      (location) => location?.continent == continent,
    )?.[0]?.position ?? {
      latitude: DEFAULT_LATITUDE_AND_LONGITUDE[0],
      longitude: DEFAULT_LATITUDE_AND_LONGITUDE[1],
    };

    // get center + zoom amount from active continent
    const { center, zoom } = getLngLatAndZoomBasedOnActiveContinent(
      continent,
      geopoint,
    );

    // Remove all markers from map
    document
      .querySelectorAll(".maplibregl-marker")
      .forEach((element) => element.remove());

    // Fly to continent
    map.flyTo({
      center: center.reverse() as [number, number],
      zoom: zoom,
      essential: true, // this animation is considered essential with respect to prefers-reduced-motion
      animate: true,
      curve: 1,
    });

    // Add markers for either continent (if flying to All Continents) or add markers for locations for selected continent
    if (continent == "All facilities") {
      addContinentMarkers(locations, map);
      resetToWorldView(map);
      map.dragPan.disable();
    } else {
      // get continent locations
      const locationsOfActiveContinent = locations?.filter(
        (location) => location.continent == continent,
      );
      addLocationMarkersOnTheMap(locationsOfActiveContinent, map);
      window.innerWidth < 1024 ? map.dragPan.enable() : map.dragPan.disable();
    }
  }

  function addMarkersOfType(type: LocationType, map: maplibregl.Map) {
    // Remove all markers from map
    document
      .querySelectorAll(".maplibregl-marker")
      .forEach((element) => element.remove());

    const typeToUse = type ? type : "Facility";
    // Go through all locations and only show them if they are of the correct
    // type
    const locationsByType = locations?.filter(
      (location) => location.type?.[0] === typeToUse,
    );
    addLocationMarkersOnTheMap(locationsByType ?? [], map);
  }

  function showLocationType(locationType: LocationType, map: maplibregl.Map) {
    resetToWorldView(map);
    addMarkersOfType(locationType, map);
  }

  function resetToWorldView(map: maplibregl.Map) {
    // Move the map so the initial state shows the whole world taking up the
    // whole width/height of the canvas element.
    const paddingObjects = {
      mobile: { top: 0, left: 60, bottom: 0, right: 60 },
      tablet: { top: 45, left: 140, bottom: 45, right: 140 },
      desktop: { top: 50, left: 100, bottom: 50, right: 100 },
    };
    const windowSize =
      window.innerWidth < 768
        ? "mobile"
        : window.innerWidth < 1024
          ? "tablet"
          : "desktop";
    const paddingObjectToUse =
      paddingObjects[windowSize] || paddingObjects.mobile;

    // map.fitBounds(
    //   [
    //     [-180, -85],
    //     [180, 85],
    //   ],
    //   { padding: paddingObjectToUse },
    // );
  }

  useEffect(() => {
    let map: maplibregl.Map | undefined;

    if (mapContainer.current && mapContainer.current !== null) {
      const protocol = new Protocol();
      maplibregl.addProtocol("pmtiles", protocol.tile);

      // Start map off at default longitude and latitude and zoom, which is directly
      // After 1024+ we use default zoom,
      const mapZoom =
        window.innerWidth < 1280
          ? window.innerWidth < 768
            ? -0.5
            : 0.5
          : DEFAULT_ZOOM;
      map = new maplibregl.Map({
        container: mapContainer.current,
        style: {
          version: 8,
          sources: {
            newcold: {
              type: "vector",
              url: `pmtiles://${window.location.origin}/map/world-map_max-zoom-6.pmtiles`,
            },
          },
          layers: [
            {
              id: "background",
              type: "background",
              paint: {
                "background-color": "hsla(0, 100%, 100%, 0.4)",
              },
            },
            {
              id: "water",
              type: "fill",
              source: "newcold",
              "source-layer": "water",
              paint: { "fill-color": "hsl(220, 100%, 14%)" },
            },
          ],
        },
        center: DEFAULT_LATITUDE_AND_LONGITUDE.reverse() as [number, number], // starting position [lng, lat]
        zoom: mapZoom, // starting zoom
        interactive: window.innerWidth < 1024 ? true : false,
        renderWorldCopies: false,
        attributionControl: false,
      });

      resetToWorldView(map);
      map.dragPan.disable();
      // Add markers for default (which are the continents);
      addContinentMarkers(locations, map);

      // Add emitter to check if activeContinent changes, if so: then fly to globe or continent
      emitter.on("flyToContinent", (continent) =>
        checkToFlyToContinent(continent as Continent, map!),
      );
      emitter.on("showLocationType", (locationType) =>
        showLocationType(locationType as LocationType, map!),
      );
      const url = new URL(window.location.toString());
      const geoParam = url.searchParams.get("geo");
      const paramIsValid = locations?.filter(
        (location) => location?.continent === geoParam,
      )[0];
      if (geoParam && paramIsValid) {
        checkToFlyToContinent(geoParam as Continent, map);
      }
    }

    return () => {
      emitter.off("flyToContinent", (continent) =>
        checkToFlyToContinent(continent as Continent, map!),
      );
      map?.remove();
      maplibregl.removeProtocol("pmtiles");
    };
  }, []);

  return (
    <div
      className={clsx(
        "relative col-span-4 flex min-h-[320px]  flex-col md:col-span-8 md:min-h-[550px] lg:col-span-9 lg:col-start-4",
        isLocationOverview && "lg:sticky lg:top-0",
      )}
    >
      <div ref={mapContainer} className="h-80 w-full md:h-[550px]"></div>
      <div className="map-radial-gradient pointer-events-none absolute left-0 top-0 z-10 block h-80 w-full md:h-[550px]"></div>
      {!isLocationOverview && (
        <span className="mt-8 px-5 md:hidden md:px-0">
          <CallToAction to="./facilities" isOnDarkBackground isFilled={false}>
            All facilities
          </CallToAction>
        </span>
      )}
    </div>
  );
}
